import React from "react";
import ReactDOM from "react-dom/client";
import App from "./components/views/App/App.jsx";
import { BrowserRouter } from "react-router-dom";
import { UploadStatusProvider } from "./utils/UploadStatusContext";
import { DocumentTypesProvider } from "./contexts/DocumentTypesContext.jsx";
import { UserPermissionsProvider } from './contexts/UserPermissionsContext';
import { setAPIBaseURL, setLoginURL } from "./utils/api";
import { initAnalytics, identifyUser } from "./utils/analytics";
import { OfflineProvider } from "./offline/OfflineProvider";
import { loadLastEnv, saveLastEnv, fetchEnvWithTimeout } from "./offline/envStore";
import { probeAuth, renderOfflineEmpty, renderOfflineUnlock } from "./offline/boot";
import { registerOfflineSW, postTenantToSW, applyKillSwitch, bridgeKeysToSW, clearSWKeys } from "./offline/swRegistration";
import { setEncryptionMode, getKeys, onKeys } from "./offline/secure.js";
import { fetchOnlineKey, dropPlainCopy, loadWrap, unlockWithPassword } from "./offline/offlineKeys.js";
import { hasSnapshot } from "./offline/snapshotProbe";
import { isOfflineLocked, unlockOffline } from "./offline/offlineLock";
import { preloadSealed } from "./offline/sealedLocal.js";
import "./assets/styles/index.css";
import "./assets/styles/badges.css";
import "./offline/offline.css";

// Console → server logging removed: the previous implementation overrode
// console.log/error to POST every log to /api/log, causing UI freezes.

/* -------------------------------------------------------------------------- */
/* SPA bootstrapping                                                          */
/* -------------------------------------------------------------------------- */
async function startApp({ offline, env }) {
  // Chat/search history: decrypted once here so components can read it synchronously.
  await preloadSealed();
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <OfflineProvider initialOnline={!offline} env={env}>
      <UserPermissionsProvider>
        <DocumentTypesProvider>
          <UploadStatusProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </UploadStatusProvider>
        </DocumentTypesProvider>
      </UserPermissionsProvider>
    </OfflineProvider>
  );
}

/* -------------------------------------------------------------------------- */
/* Boot                                                                       */
/*                                                                            */
/* 1. /api/env with a timeout; on failure fall back to the remembered env     */
/*    (localStorage od.lastEnv) and enter offline mode.                       */
/* 2. Register the service worker (or apply the kill switch).                 */
/* 3. Online: probe /api/me. verifyToken never answers 401 — it 302s to the   */
/*    login app, visible here as `opaqueredirect` → that (and only that) is   */
/*    a real auth failure. A network error is NOT a login redirect anymore:   */
/*    it flips us into offline mode.                                          */
/* 4. Offline without a snapshot → explanatory page, never blank.             */
/* -------------------------------------------------------------------------- */
async function boot() {
  const root = document.getElementById("root");

  // Strip cache-buster login_t query param from URL after load
  if (window.location.search.includes("login_t=")) {
    try { window.history.replaceState(null, "", window.location.pathname); } catch { /* ignore */ }
  }

  const lastEnv = loadLastEnv();
  let env = null;
  let offline = false;
  try {
    env = await fetchEnvWithTimeout(4000);
    saveLastEnv(env);
  } catch (error) {
    console.warn("[boot] /api/env unreachable — using remembered env:", error?.message || error);
    env = lastEnv;
    offline = true;
  }

  if (!env) {
    renderOfflineEmpty(
      root,
      "Sie sind offline und es sind noch keine Daten gespeichert. Bitte einmal mit Internetverbindung anmelden."
    );
    return;
  }

  // Use relative paths (via vite proxy) when on localhost to avoid cross-origin issues
  const base = window.location.hostname === "localhost" ? "" : (env.API_BASE_URL || "");
  setAPIBaseURL(base);
  setLoginURL(env.LOGIN_URL);
  initAnalytics(env.POSTHOG_KEY, env.POSTHOG_HOST, { tenant: env.TENANT });

  const offlineEnabled = env.OFFLINE_ENABLED !== false;
  // Encrypted offline copy: fixed for this page load, before anything opens IndexedDB.
  const encrypted = offlineEnabled && env.OFFLINE_ENCRYPTION === true;
  setEncryptionMode(encrypted);
  if (encrypted && env.TENANT) await dropPlainCopy(env.TENANT);
  if (offlineEnabled) {
    registerOfflineSW();
    postTenantToSW(env.TENANT, encrypted);
    if (encrypted) {
      // The SW may still hold the key of an earlier session: this page has none yet.
      await clearSWKeys();
      bridgeKeysToSW(getKeys, onKeys);
    }
  } else {
    await applyKillSwitch();
  }

  if (!offline) {
    const auth = await probeAuth(base);
    if (auth === "unauthenticated") {
      window.location.href = env.LOGIN_URL;
      return;
    }
    if (auth === "network-error") {
      offline = true;
    } else {
      // A verified online session is the only thing that lifts a logout lock.
      unlockOffline(env.TENANT);
      // Encrypted copy: the data key comes from the server (memory only). Without
      // it the app still runs online; the offline sync just pauses.
      if (encrypted) await fetchOnlineKey(env.TENANT, base);
      // Fire-and-forget: identify the user for analytics.
      fetch(`${base}/api/me`, { credentials: "include", redirect: "manual" })
        .then((r) => (r.ok ? r.json() : null))
        .then((me) => { if (me && me.username) identifyUser(me.username, { tenant: me.tenant }); })
        .catch(() => {});
    }
  }

  if (offline) {
    // Someone logged out on this machine: the cached documents stay on disk,
    // but nothing may read them until a session is verified online again.
    if (isOfflineLocked(env.TENANT)) {
      renderOfflineEmpty(
        root,
        "Auf diesem Gerät wurde abgemeldet. Die gespeicherten Dokumente bleiben erhalten, sind aber erst nach einer erneuten Anmeldung mit Internetverbindung wieder zugänglich."
      );
      return;
    }
    const usable = offlineEnabled && env.TENANT && (await hasSnapshot(env.TENANT));
    if (usable && encrypted) {
      if (!loadWrap(env.TENANT)) {
        renderOfflineEmpty(
          root,
          "Sie sind offline. Der Offline-Zugriff wurde auf diesem Gerät noch nicht eingerichtet (online unter Einstellungen › Offline-Nutzung: Passwort bestätigen)."
        );
        return;
      }
      renderOfflineUnlock(root, {
        unlock: (password) => unlockWithPassword(env.TENANT, password),
        onDone: () => startApp({ offline, env }),
      });
      return;
    }
    if (!usable) {
      renderOfflineEmpty(
        root,
        offlineEnabled
          ? "Sie sind offline. Für diesen Mandanten sind noch keine Daten gespeichert – bitte einmal mit Internetverbindung anmelden und synchronisieren."
          : "Der Server ist nicht erreichbar und die Offline-Funktion ist deaktiviert."
      );
      return;
    }
  }

  startApp({ offline, env });
}

boot();
